import { getNowPlainDateISO } from '@/lib/time-zones.js';
import {
  emptySelectOption,
  requiredMultiSelectOptions,
  requiredSelectOption,
  selectOption,
} from '@/components/ui/multi-select-utils.js';
import {
  PendingStudentGroupReassignmentRequest,
  PreferredCoachIneligibleReasonLabelMap,
  StudentGroupReassignmentReason,
  StudentGroupReassignmentReasonLabelMap,
} from '@airreading/common/data';
import { DefaultValues } from 'react-hook-form';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

export const specialRequirementsMaxLength = 150;
export const reassignmentNotesMaxLength = 500;

export const reassignmentRequestFormSchema = z.object({
  effectiveDate: z.instanceof(Temporal.PlainDate, { message: 'Effective date is required' }),
  requester: requiredSelectOption(),
  reassignmentReasons: requiredMultiSelectOptions('Select at least one reassignment reason'),
  preferredNewTeacher: selectOption,
  specialRequirements: z
    .string()
    .max(
      specialRequirementsMaxLength,
      `Special requirements must be ${specialRequirementsMaxLength} characters or fewer.`,
    )
    .optional(),
  countAsReassignedFromTestTeacher: z.boolean(),
});
export type ReassignmentRequestFormValues = z.infer<typeof reassignmentRequestFormSchema>;

export function getReassignmentRequestFormDefaultValues(
  preferredTimeZone: string | null | undefined,
  overrides: Partial<ReassignmentRequestFormValues> = {},
): DefaultValues<ReassignmentRequestFormValues> {
  return {
    effectiveDate: getNowPlainDateISO(preferredTimeZone),
    requester: emptySelectOption,
    reassignmentReasons: [],
    preferredNewTeacher: emptySelectOption,
    specialRequirements: '',
    countAsReassignedFromTestTeacher: false,
    ...overrides,
  };
}

export const bulkReassignTeacherFormSchema = z.object({
  updateType: z.string(),
  startPlainDate: z.instanceof(Temporal.PlainDate),
  endPlainDate: z.instanceof(Temporal.PlainDate),
  teacherType: z.string().optional(),
  coachAvailable: selectOption.optional(),
  coachAll: selectOption.optional(),
  specialRequirements: z
    .string()
    .max(
      specialRequirementsMaxLength,
      `Special requirements must be ${specialRequirementsMaxLength} characters or fewer.`,
    )
    .optional(),
  reassignmentNotes: z
    .string()
    .max(reassignmentNotesMaxLength, `Reassignment notes must be ${reassignmentNotesMaxLength} characters or fewer.`)
    .optional(),
  /** Synced by the form from live system-suggested coaches (post-refresh). */
  requiresReassignmentNotes: z.boolean().optional(),
  protectedPeriodOverrideReason: z.string().optional(),
});

export function getDefaultSpecialRequirements(reassignmentRequests: PendingStudentGroupReassignmentRequest[]): string {
  const values = reassignmentRequests.map((request) => request.specialRequirements?.trim() ?? '');
  const uniqueValues = [...new Set(values)];
  return uniqueValues.length === 1 ? uniqueValues[0]! : '';
}

/** True when a selected teacher differs from any request's system suggested teacher. */
export function shouldShowReassignmentNotes(
  reassignmentRequests: PendingStudentGroupReassignmentRequest[],
  selectedCoachId: string | undefined | null,
): boolean {
  if (!selectedCoachId) {
    return false;
  }

  return reassignmentRequests.some(
    (request) => request.systemSuggestedCoach != null && request.systemSuggestedCoach.id !== selectedCoachId,
  );
}

/**
 * Attention-needed messages for the Reassign teacher modal, in AC display order.
 */
export function getReassignmentAttentionNeededMessages(
  reassignmentRequests: PendingStudentGroupReassignmentRequest[],
): string[] {
  const messages: string[] = [];

  if (reassignmentRequests.some((request) => !request.systemSuggestedCoach)) {
    messages.push('No eligible teacher found');
  }

  for (const request of reassignmentRequests) {
    const specialRequirements = request.specialRequirements?.trim();
    if (specialRequirements) {
      const message = `Special requirements: ${specialRequirements}`;
      if (!messages.includes(message)) {
        messages.push(message);
      }
    }
  }

  // priorCount excludes the current request, so >= 2 means the group has ≥ 3 total.
  if (reassignmentRequests.some((request) => request.priorReassignmentRequestCount >= 2)) {
    messages.push('Student group has ≥ 3 reassignment requests.');
  }

  if (
    reassignmentRequests.some((request) =>
      request.reasons.includes(StudentGroupReassignmentReason.student_leveling_issue),
    )
  ) {
    messages.push(`Reassignment reason: ${StudentGroupReassignmentReasonLabelMap.student_leveling_issue}`);
  }

  if (
    reassignmentRequests.some((request) => request.reasons.includes(StudentGroupReassignmentReason.school_complaint))
  ) {
    messages.push(`Reassignment reason: ${StudentGroupReassignmentReasonLabelMap.school_complaint}`);
  }

  return messages;
}

export function getUniqueSystemSuggestedCoachIds(
  reassignmentRequests: PendingStudentGroupReassignmentRequest[],
): string[] {
  return [
    ...new Set(
      reassignmentRequests.map((request) => request.systemSuggestedCoach?.id).filter((id): id is string => !!id),
    ),
  ];
}

/** Shown under Available teachers when preferred exists but was not chosen as system suggested. */
export function getPreferredTeacherUnavailableHint(
  reassignmentRequests: PendingStudentGroupReassignmentRequest[],
): string | null {
  const labels = [
    ...new Set(
      reassignmentRequests
        .map((request) => request.preferredCoachIneligibleReason)
        .filter((reason): reason is NonNullable<typeof reason> => reason != null)
        .map((reason) => PreferredCoachIneligibleReasonLabelMap[reason]),
    ),
  ];

  if (labels.length === 0) {
    return null;
  }

  return labels.join('; ');
}
